import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, IconButton, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Alert,
    useMediaQuery, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import api from '../../services/api';
import { isVisualizador } from '../../services/auth';
import { toastSuccess, alertValidacao, confirmAcao } from '../../services/alerts';
import { formatarMoeda, formatarData, MODALIDADE_LABEL } from '../../services/convenios';

const estadoInicial = () => ({
    codigoProcedimento: '', descricao: '', valor: '',
    modalidadeSessao: '', vigenciaInicio: '', vigenciaFim: '',
});

export default function ProcedimentosConvenio() {
    const navigate = useNavigate();
    const { id } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const soLeitura = isVisualizador();

    const [convenio, setConvenio] = useState(null);
    const [procedimentos, setProcedimentos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [dialogAberto, setDialogAberto] = useState(false);
    const [editandoId, setEditandoId] = useState(null);
    const [form, setForm] = useState(estadoInicial());

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [conv, procs] = await Promise.all([
                api.get(`/planos-saude/${id}`),
                api.get(`/planos-saude/${id}/procedimentos`),
            ]);
            setConvenio(conv.data);
            setProcedimentos(Array.isArray(procs.data) ? procs.data : []);
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { carregar(); }, [carregar]);

    const abrirNovo = () => {
        setEditandoId(null);
        setForm(estadoInicial());
        setDialogAberto(true);
    };

    const abrirEdicao = (item) => {
        setEditandoId(item.id_procedimento_convenio);
        setForm({
            codigoProcedimento: item.codigoProcedimento || '',
            descricao: item.descricao || '',
            valor: item.valor ?? '',
            modalidadeSessao: item.modalidadeSessao || '',
            vigenciaInicio: item.vigenciaInicio || '',
            vigenciaFim: item.vigenciaFim || '',
        });
        setDialogAberto(true);
    };

    const set = (campo) => (e) => setForm(prev => ({ ...prev, [campo]: e.target.value }));

    const handleSalvar = async () => {
        if (!form.codigoProcedimento.trim()) { alertValidacao('Informe o código do procedimento.'); return; }
        if (!form.descricao.trim()) { alertValidacao('Informe a descrição do procedimento.'); return; }
        if (form.valor === '' || Number(form.valor) < 0) { alertValidacao('Informe um valor válido.'); return; }
        if (!form.vigenciaInicio) { alertValidacao('Informe o início da vigência.'); return; }
        if (form.vigenciaFim && form.vigenciaFim < form.vigenciaInicio) {
            alertValidacao('O fim da vigência não pode ser anterior ao início.');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                codigoProcedimento: form.codigoProcedimento.trim(),
                descricao: form.descricao.trim(),
                valor: Number(form.valor),
                modalidadeSessao: form.modalidadeSessao || null,
                vigenciaInicio: form.vigenciaInicio,
                vigenciaFim: form.vigenciaFim || null,
            };
            if (editandoId) {
                await api.put(`/planos-saude/${id}/procedimentos/${editandoId}`, payload);
                toastSuccess('Valor atualizado');
            } else {
                await api.post(`/planos-saude/${id}/procedimentos`, payload);
                toastSuccess('Valor cadastrado');
            }
            setDialogAberto(false);
            carregar();
        } catch {
            /* erro exibido pelo handler global — inclui conflito de vigência */
        } finally {
            setSaving(false);
        }
    };

    const handleExcluir = async (item) => {
        const ok = await confirmAcao({
            title: 'Excluir valor',
            text: `Remover o valor de "${item.codigoProcedimento}" vigente desde ${formatarData(item.vigenciaInicio)}? Guias já emitidas guardam a própria cópia do valor e não mudam.`,
            confirmButtonText: 'Excluir',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/planos-saude/${id}/procedimentos/${item.id_procedimento_convenio}`);
            toastSuccess('Valor excluído');
            carregar();
        } catch {
            /* erro exibido pelo handler global */
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate('/convenios')} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                        Tabela de valores
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {convenio?.nome}
                    </Typography>
                </Box>
                {!soLeitura && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo}>
                        Novo valor
                    </Button>
                )}
            </Box>

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                    O valor da guia é resolvido por código, data do atendimento e modalidade — nunca digitado na
                    emissão. Reajustar a tabela não altera guias já emitidas, que guardam a própria cópia do valor.
                </Alert>

                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size={isMobile ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell><strong>Código</strong></TableCell>
                                <TableCell><strong>Descrição</strong></TableCell>
                                <TableCell align="right"><strong>Valor</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Modalidade</strong></TableCell>
                                <TableCell><strong>Vigência</strong></TableCell>
                                {!soLeitura && <TableCell align="right"><strong>Ações</strong></TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {procedimentos.length > 0 ? procedimentos.map((item) => (
                                <TableRow key={item.id_procedimento_convenio} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                                    <TableCell>{item.codigoProcedimento}</TableCell>
                                    <TableCell>{item.descricao}</TableCell>
                                    <TableCell align="right">{formatarMoeda(item.valor)}</TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                        {item.modalidadeSessao ? (MODALIDADE_LABEL[item.modalidadeSessao] || item.modalidadeSessao) : 'Qualquer'}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="body2">
                                                {formatarData(item.vigenciaInicio)} — {item.vigenciaFim ? formatarData(item.vigenciaFim) : 'sem fim'}
                                            </Typography>
                                            <Chip size="small" label={item.vigente ? 'Vigente' : 'Fora de vigência'}
                                                color={item.vigente ? 'success' : 'default'} />
                                        </Box>
                                    </TableCell>
                                    {!soLeitura && (
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => abrirEdicao(item)}>
                                                <EditIcon fontSize="small" color="primary" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleExcluir(item)}>
                                                <DeleteIcon fontSize="small" color="error" />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={soLeitura ? 5 : 6} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" sx={{ color: '#999' }}>
                                            Nenhum valor cadastrado. Sem tabela de valores, a emissão de guia deste
                                            convênio é recusada.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={dialogAberto} onClose={() => setDialogAberto(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editandoId ? 'Editar valor' : 'Novo valor'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField sx={{ flex: 1, minWidth: 140 }} label="Código *" value={form.codigoProcedimento}
                                onChange={set('codigoProcedimento')} placeholder="Ex: 50000470" />
                            <TextField sx={{ flex: 1, minWidth: 140 }} type="number" label="Valor *" value={form.valor}
                                onChange={set('valor')} inputProps={{ min: 0, step: '0.01' }} />
                        </Box>
                        <TextField label="Descrição *" value={form.descricao} onChange={set('descricao')}
                            placeholder="Ex: Sessão de psicoterapia individual" />
                        <TextField select label="Modalidade" value={form.modalidadeSessao} onChange={set('modalidadeSessao')}
                            helperText="Vazio = vale para qualquer modalidade.">
                            <MenuItem value="">Qualquer</MenuItem>
                            <MenuItem value="PRESENCIAL">Presencial</MenuItem>
                            <MenuItem value="ONLINE">Online</MenuItem>
                        </TextField>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField sx={{ flex: 1, minWidth: 160 }} type="date" label="Vigência início *"
                                InputLabelProps={{ shrink: true }} value={form.vigenciaInicio} onChange={set('vigenciaInicio')} />
                            <TextField sx={{ flex: 1, minWidth: 160 }} type="date" label="Vigência fim"
                                InputLabelProps={{ shrink: true }} value={form.vigenciaFim} onChange={set('vigenciaFim')}
                                helperText="Vazio = vigência aberta." />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogAberto(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSalvar} disabled={saving}>
                        {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
